import { defineStore } from 'pinia';
import authApi from '../api/auth';
import usersApi from '../api/users';
import { connectSocket, disconnectSocket } from '../api/socket';
import { useUiStore } from './ui';

// Module-level (not store state) so it survives across calls without
// triggering reactivity; collapses concurrent fetchMe() callers (router
// guard + any other caller) into a single in-flight request.
let fetchMePromise = null;

function applyUserAppearance(user) {
  const uiStore = useUiStore();
  uiStore.setAppearance({
    theme: user?.theme,
    themeAccent: user?.themeAccent,
  }, {
    touch: false,
    userId: user?._id || user?.id,
  });
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('bm_token') || null,
    hydrated: false,
    pendingAppearance: null,
  }),
  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    isAdmin: (state) => state.user?.role === 'admin',
    hasActiveSubscription: (state) => {
      if (!state.user?.subscription) return false;
      const { status, currentPeriodEnd } = state.user.subscription;
      return status === 'active' && currentPeriodEnd && new Date(currentPeriodEnd) > new Date();
    },
  },
  actions: {
    async register(payload) {
      const { user, token } = await authApi.register(payload);
      this.setSession(user, token);
    },
    async login(payload) {
      const { user, token } = await authApi.login(payload);
      this.setSession(user, token);
    },
    async fetchMe() {
      if (!this.token) {
        this.hydrated = true;
        return;
      }
      if (fetchMePromise) return fetchMePromise;
      fetchMePromise = (async () => {
        try {
          const { user } = await authApi.me();
          const uiStore = useUiStore();
          const localAppearance = uiStore.getLocalAppearance();
          const localAppearanceIsNewer = uiStore.isLocalAppearanceNewerThan(user);
          const effectiveAppearance = this.pendingAppearance || (localAppearanceIsNewer ? localAppearance : null);

          this.user = effectiveAppearance ? { ...user, ...effectiveAppearance } : user;
          applyUserAppearance(this.user);
          connectSocket(this.token);

          if (localAppearanceIsNewer && !this.pendingAppearance) {
            usersApi.updateProfile(localAppearance)
              .then(({ user: syncedUser }) => {
                this.user = { ...syncedUser, ...localAppearance };
                applyUserAppearance(this.user);
              })
              .catch(() => {});
          }
        } catch {
          this.logout();
        } finally {
          this.hydrated = true;
        }
      })();
      try {
        await fetchMePromise;
      } finally {
        fetchMePromise = null;
      }
    },
    setSession(user, token) {
      const uiStore = useUiStore();
      const localAppearance = uiStore.getLocalAppearance();
      const localAppearanceIsNewer = uiStore.isLocalAppearanceNewerThan(user);
      this.user = localAppearanceIsNewer ? { ...user, ...localAppearance } : user;
      this.token = token;
      this.hydrated = true;
      localStorage.setItem('bm_token', token);
      applyUserAppearance(this.user);
      connectSocket(token);
    },
    setToken(token) {
      this.token = token;
      this.hydrated = false;
      localStorage.setItem('bm_token', token);
      connectSocket(token);
    },
    async updateProfile(payload) {
      const appearancePayload = {};
      if (payload.theme) appearancePayload.theme = payload.theme;
      if (payload.themeAccent) appearancePayload.themeAccent = payload.themeAccent;
      const hasAppearanceUpdate = Object.keys(appearancePayload).length > 0;
      if (hasAppearanceUpdate) {
        useUiStore().markAppearanceOwner(this.user?._id || this.user?.id);
        this.pendingAppearance = { ...(this.pendingAppearance || {}), ...appearancePayload };
      }
      const { user } = await usersApi.updateProfile(payload);
      this.user = hasAppearanceUpdate ? { ...user, ...appearancePayload } : user;
      if (hasAppearanceUpdate) this.pendingAppearance = null;
      applyUserAppearance(this.user);
    },
    async uploadAvatar(file) {
      const { user } = await usersApi.uploadAvatar(file);
      this.user = user;
    },
    logout() {
      this.user = null;
      this.token = null;
      this.hydrated = true;
      this.pendingAppearance = null;
      localStorage.removeItem('bm_token');
      disconnectSocket();
    },
  },
});
